import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../contexts/LanguageContext';
import LocalizedLink from '../components/common/LocalizedLink';
import SEO from '../components/common/SEO';
import { articlesEn } from '../data/articles/en/index';
import { articlesZh } from '../data/articles/zh/index';
import { articlesJa } from '../data/articles/ja/index';

const { FiSearch, FiCalendar, FiClock, FiArrowRight, FiStar } = FiIcons;

const BlogPage = () => {
  const { t, language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Select articles based on current language
  const articles = useMemo(() => {
    switch (language) {
      case 'zh': return articlesZh;
      case 'ja': return articlesJa;
      default: return articlesEn;
    }
  }, [language]);

  // Get featured article
  const featuredArticle = useMemo(() => {
    return articles.find(article => article.featured);
  }, [articles]);

  // Filter and sort articles
  const filteredArticles = useMemo(() => {
    let filtered = articles;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Sort articles
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.publishDate) - new Date(a.publishDate);
      } else if (sortBy === 'oldest') {
        return new Date(a.publishDate) - new Date(b.publishDate);
      }
      return 0;
    });

    return filtered;
  }, [articles, searchQuery, selectedCategory, sortBy]);

  const categories = [
    { value: 'all', label: t('blog.categories.all') },
    { value: 'health', label: t('blog.categories.health') },
    { value: 'nutrition', label: t('blog.categories.nutrition') },
    { value: 'training', label: t('blog.categories.training') },
    { value: 'grooming', label: t('blog.categories.grooming') },
    { value: 'lifestyle', label: t('blog.categories.lifestyle') },
    { value: 'news', label: t('blog.categories.news') }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : language === 'zh' ? 'zh-TW' : 'ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <SEO
        title={t('common.seo.blog.title')}
        description={t('common.seo.blog.description')}
        keywords={t('common.seo.blog.keywords')}
        type="website"
      />

      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/20 pt-20">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-12 md:py-16 overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4">
                {t('blog.pageTitle')}
              </h1>
              <p className="text-base md:text-lg text-primary-100">
                {t('blog.pageSubtitle')}
              </p>
            </motion.div>
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* Featured Article */}
          {featuredArticle && (
            <motion.div
              key={`featured-${language}-${featuredArticle.slug}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiStar} className="h-5 w-5 text-primary-600 mr-2" />
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {t('blog.featured')} {t('blog.article')}
                </h2>
              </div>
              
              <LocalizedLink to={`/blog/${featuredArticle.slug}`} className="block group">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* Featured Image */}
                    <div className="relative h-64 md:h-full overflow-hidden">
                      <img
                        src={featuredArticle.image}
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute top-4 left-4 bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                        {t('blog.featured')}
                      </div>
                    </div>

                    {/* Featured Content */}
                    <div className="p-8 md:p-10 flex flex-col justify-center">
                      <div className="mb-4">
                        <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                          {categories.find(c => c.value === featuredArticle.category)?.label}
                        </span>
                      </div>
                      
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-primary-600 transition-colors">
                        {featuredArticle.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-6 line-clamp-3 text-base">
                        {featuredArticle.excerpt}
                      </p>

                      <div className="flex items-center text-sm text-gray-500 mb-6 space-x-4">
                        <div className="flex items-center">
                          <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1.5" />
                          <span>{formatDate(featuredArticle.publishDate)}</span>
                        </div>
                        <div className="flex items-center">
                          <SafeIcon icon={FiClock} className="h-4 w-4 mr-1.5" />
                          <span>{featuredArticle.readTime} {t('blog.minutesRead')}</span>
                        </div>
                      </div>

                      <div className="flex items-center text-primary-600 font-semibold">
                        <span>{t('blog.readMore')}</span>
                        <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
                      </div>
                    </div>
                  </div>
                </div>
              </LocalizedLink>
            </motion.div>
          )}

          {/* Search and Filter Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mb-8 space-y-4"
          >
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder={t('blog.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow bg-white shadow-sm"
                />
              </div>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white cursor-pointer shadow-sm"
              >
                <option value="newest">{t('blog.sortNewest')}</option>
                <option value="oldest">{t('blog.sortOldest')}</option>
              </select>
            </div>

            {/* Categories - Horizontal Scroll on Mobile */}
            <div className="relative">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category.value
                        ? 'bg-primary-600 text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-primary-300 hover:shadow-md'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Results Count */}
          <AnimatePresence mode="wait">
            {(searchQuery || selectedCategory !== 'all') && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-6"
              >
                <p className="text-gray-600 text-sm">
                  {t('blog.showing')} <span className="font-semibold text-gray-900">{filteredArticles.length}</span> {filteredArticles.length === 1 ? t('blog.article') : t('blog.articles')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredArticles.map((article, index) => (
                <motion.div
                  key={`${language}-${article.slug}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  layout
                >
                  <LocalizedLink
                    to={`/blog/${article.slug}`}
                    className="block group h-full"
                  >
                    <article className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2 cursor-pointer h-full flex flex-col">
                      {/* Article Image */}
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>

                      {/* Article Content */}
                      <div className="p-6 flex-1 flex flex-col">
                        {/* Category Badge */}
                        <div className="mb-3">
                          <span className="inline-block px-3 py-1 bg-gradient-to-r from-primary-100 to-primary-50 text-primary-700 rounded-full text-xs font-medium">
                            {categories.find(c => c.value === article.category)?.label}
                          </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {article.title}
                        </h2>

                        {/* Excerpt */}
                        <p className="text-gray-600 mb-4 line-clamp-2 text-sm flex-1 leading-relaxed">
                          {article.excerpt}
                        </p>

                        {/* Meta Info */}
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
                          <div className="flex items-center">
                            <SafeIcon icon={FiCalendar} className="h-3.5 w-3.5 mr-1" />
                            <span>{formatDate(article.publishDate)}</span>
                          </div>
                          <div className="flex items-center">
                            <SafeIcon icon={FiClock} className="h-3.5 w-3.5 mr-1" />
                            <span>{article.readTime} {t('blog.minutesRead')}</span>
                          </div>
                        </div>

                        {/* Read More Indicator */}
                        <div className="mt-4 flex items-center text-primary-600 font-semibold text-sm">
                          <span>{t('blog.readMore')}</span>
                          <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                        </div>
                      </div>
                    </article>
                  </LocalizedLink>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20 bg-white rounded-2xl shadow-md"
            >
              <div className="mb-6">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                  <SafeIcon icon={FiSearch} className="h-10 w-10 text-gray-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {t('blog.noArticlesFound')}
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {t('blog.noArticlesDescription')}
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg"
              >
                {t('blog.clearFilters')}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPage;